import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { ChangeLogButton } from '@app/components/ChangeLogButton';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { RemoveButton } from '@app/components/RemoveButton';
import { useBreadcrumbLabel } from "@app/hooks/useBreadcrumbLabel";
import { useEntity } from '@app/hooks/useEntity';
import { PrivateCourse } from '@app/model/PrivateCourse'; // Make sure this path is correct
import { usePrivateCourseStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, AlertVariant, Bullseye, Button, Card, CardBody, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Form, FormGroup, Grid, GridItem, Icon, PageSection, Pagination, PaginationVariant, SearchInput, Split, SplitItem, TextInput, Title, Toolbar, ToolbarContent, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

const emptyPrivateCourse: Partial<PrivateCourse> = {
  sku: '', description: ''
};

const PrivateCourses: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  useBreadcrumbLabel("Private Courses");

  // --- Core State ---
  const { items: privateCourses, error, retry } = useEntity(usePrivateCourseStore);
  const save = usePrivateCourseStore( state => state.save );
  const remove = usePrivateCourseStore( state => state.delete );
  
  // --- Form State ---
  const [formData, setFormData] = React.useState<Partial<PrivateCourse>>(emptyPrivateCourse);
  /**
   * The row whose change log is open, or null for none.
   *
   * A normalised {id, label} rather than the row itself: these three pages have no detail page, so
   * the panel is driven from here, and each of them names its rows with a different field.
   */
  const [changeLogRow, setChangeLogRow] = React.useState<{ id: number; label: string } | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isNew, setIsNew] = React.useState(false);

  // --- UI State ---
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('private-courses', 10);
  const [filterText, setFilterText] = React.useState('');

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(() => {
    if (!filterText.trim()) return privateCourses;
    const lowerFilter = filterText.toLowerCase();
    return privateCourses.filter(p => 
      p.sku.toLowerCase().includes(lowerFilter) || 
      p.description.toLowerCase().includes(lowerFilter)
    );
  }, [privateCourses, filterText]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- Form Handlers ---
  const handleInputChange = (value: string, event: React.FormEvent<HTMLInputElement>) => {
    const name = event.currentTarget.name;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onNewClick = () => {
    setFormData(emptyPrivateCourse);
    setIsEditing(true);
    setIsNew(true);
  };

  const onEditClick = (course: PrivateCourse) => {
    setFormData(course);
    setIsEditing(true);
    setIsNew(false);
  };

  const onCancelClick = () => {
    setFormData(emptyPrivateCourse);
    setIsEditing(false);
    setIsNew(false);
  };

  // --- CRUD Operations ---
  const onSaveClick = async () => {
    if (!formData.sku || !formData.description) {
      addNotification('Validation Error', AlertVariant.warning, 'SKU and Description are required.');
      return;
    }

    beginLoading();
    try {
      await save(formData, isNew);

      if (isNew) {
        addNotification('Private Course Created', AlertVariant.success, `${formData.sku} was successfully added.`);
      } else {
        addNotification('Private Course Updated', AlertVariant.success, `${formData.sku} was successfully updated.`);
      }
      setIsEditing(false);
      setIsNew(false);
      setFormData(emptyPrivateCourse);
    } catch (error: any) {
      addNotification('Error saving private course', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onRemoveConfirm = async (id: string | number) => {
    beginLoading();
    try {
      await remove(id);

      addNotification('Private Course Removed', AlertVariant.info, 'The private course has been deleted.');
      
      // Clear form if the user deleted the item they were currently editing
      if (formData.id === id) {
        onCancelClick();
      }
    } catch (error: any) {
      addNotification('Error deleting private course', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  // --- UI Helpers ---
  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact={true}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  return (
    <EntityChangeLog
      entity="privatecourse"
      id={changeLogRow?.id}
      entityLabel="this private course"
      title={changeLogRow?.label}
      isOpen={!!changeLogRow}
      onClose={() => setChangeLogRow(null)}
    >
      {/* Header Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Private Courses</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem hidden={!isEditing}>
                <Button variant="primary" onClick={onSaveClick}>
                  Save
                </Button>
              </ActionListItem>
              <ActionListItem hidden={!isEditing}>
                <Button variant="link" onClick={onCancelClick}>
                  Cancel
                </Button>
              </ActionListItem>
              <ActionListItem hidden={isEditing}>
                <Button variant="primary" onClick={onNewClick}>
                  New Private Course
                 </Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {/* Editor Form Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card isPlain className='form-same-list-page'>
          <CardBody>
            <Form>
              {/* Used md={6} instead of md={4} because there are only 2 fields */}
              <Grid hasGutter md={6}>
                <GridItem>
                  <FormGroup label="SKU" isRequired fieldId="sku">
                    <TextInput isRequired type="text" id="sku" name="sku" value={formData.sku || ''} onChange={(_e, val) => handleInputChange(val, _e)} isDisabled={!isEditing} placeholder="e.g. DO180" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Description" isRequired fieldId="description">
                    <TextInput isRequired type="text" id="description" name="description" value={formData.description || ''} onChange={(_e, val) => handleInputChange(val, _e)} isDisabled={!isEditing} placeholder="e.g. Red Hat OpenShift Administration I" />
                  </FormGroup>
                </GridItem>
              </Grid>
            </Form>
          </CardBody>
        </Card>
      </PageSection>

      {/* List / Table Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            {/* FADE OVERLAY WRAPPER */}
            <div style={{ 
              opacity: isEditing ? 0.4 : 1, 
              pointerEvents: isEditing ? 'none' : 'auto', 
              transition: 'opacity 0.3s ease-in-out'
            }}>
              <Toolbar id="compact-toolbar" clearAllFilters={() => setFilterText('')}>
                <ToolbarContent>
                  <ToolbarItem>
                    <SearchInput aria-label="Search private courses" placeholder="Search by SKU or description..." value={filterText} onChange={(_e, val) => { setFilterText(val); setPage(1); }} onClear={() => setFilterText('')} />
                  </ToolbarItem>
                  <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
                </ToolbarContent>
              </Toolbar>
              
              {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                  gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
              <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
                <InnerScrollContainer>
                  <Table aria-label="Private Courses Table" isStickyHeader variant='compact' gridBreakPoint="">
                    <Thead>
                      <Tr>
                        <Th>SKU</Th>
                        <Th>Description</Th>
                        <Th width={10} isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedRows.map((row) => (
                        <Tr key={row.id} isRowSelected={formData.id === row.id}>
                          <Td dataLabel="SKU"><b>{row.sku}</b></Td>
                          <Td dataLabel="Description">{row.description}</Td>
                          <Td dataLabel="Actions" modifier="nowrap"
                            isStickyColumn stickyMinWidth="160px" stickyRightOffset="0" hasLeftBorder>
                            <ActionList>
                              <ActionListItem>
                                <Tooltip content="Edit">
                                  <Button 
                                    icon={<Icon size='lg'><PencilAltIcon /></Icon>} 
                                    variant="plain" 
                                    onClick={() => onEditClick(row)} 
                                    isDisabled={isEditing} 
                                  />
                                </Tooltip>
                              </ActionListItem>
                              <ActionListItem>
                                <RemoveButton 
                                  confirmationMessage={`Remove ${row.sku}?`} 
                                  onRemoveConfirm={() => onRemoveConfirm(row.id)} 
                                  isDisabled={isEditing} 
                                />
                              </ActionListItem>
                              <ActionListItem>
                                <ChangeLogButton recordLabel={row.sku} onClick={() => setChangeLogRow({ id: row.id, label: row.sku })} />
                              </ActionListItem>
                            </ActionList>
                          </Td>
                        </Tr>
                      ))}
                      {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                      {error && filteredRows.length === 0 && (
                        <DataLoadErrorRow colSpan={3} error={error} entityLabel="private courses" onRetry={retry} />
                      )}
                      {!error && filteredRows.length === 0 && (
                        <Tr>
                          <Td colSpan={3}>
                            <Bullseye>
                              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                                <EmptyStateBody>No private courses match your search criteria.</EmptyStateBody>
                                <EmptyStateFooter>
                                  <EmptyStateActions><Button variant="link" onClick={() => setFilterText('')}>Clear search</Button></EmptyStateActions>
                                </EmptyStateFooter>
                              </EmptyState>
                            </Bullseye>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </InnerScrollContainer>
              </OuterScrollContainer>
              {buildPagination('bottom')}
            </div>
            {/* END FADE OVERLAY WRAPPER */}
          </CardBody>
        </Card>
      </PageSection>
    </EntityChangeLog>
  );
};

export { PrivateCourses };
